import * as React from 'react';
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import SettingsIcon from '@mui/icons-material/Settings';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PersonIcon from '@mui/icons-material/Person';
import { Link } from 'react-router-dom';
import './Sidebar.css';

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    '& .MuiIconButton-root': {
        color: '#FCE4E5', // Customize the icon button color (the icon inside will inherit this)
    },
    '& .MuiSvgIcon-root': {
        color: '#FCE4E5', // Customize the icon color itself
        fontSize: '1.5rem', // Change the size of the icon
    },
}));

// const DrawerFooter = styled('div')(({ theme }) => ({
//     display: 'flex',
//     alignItems: 'center',
//     justifyContent: 'center', 
//     padding: theme.spacing(2),
//     borderTop: `1px solid ${theme.palette.divider}`,
//     backgroundColor: '#902427', 
//     position: 'absolute',
//     bottom: 0,
//     width: '100%',
//   }));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': {
                ...openedMixin(theme),
                backgroundColor: theme.palette.primary.main,
                '& .MuiListItemButton-root': {
                    color: theme.palette.primary.contrastText, // Customize text color for the button
                    '&:hover': {
                        backgroundColor: theme.palette.primary.light,
                    },
                },
                '& .MuiListItemText-root': {
                    color: theme.palette.primary.contrastText, // Set font color for ListItemText
                    '& .MuiTypography-root': {
                        color: theme.palette.primary.contrastText, // Set font color for the text (key/label)
                    },
                },
                '& .MuiListItemIcon-root': {
                    color: theme.palette.primary.contrastText,
                },
                '& .MuiSvgIcon-root': {
                    color: theme.palette.primary.contrastText,
                },
            },
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': {
                ...closedMixin(theme),
                backgroundColor: theme.palette.primary.main,
                '& .MuiListItemButton-root': {
                    color: theme.palette.primary.contrastText, // Customize text color for the button
                    '&:hover': {
                        backgroundColor: theme.palette.primary.light,
                    },
                },
                '& .MuiListItemText-root': {
                    color: theme.palette.primary.contrastText, 
                },
                '& .MuiListItemIcon-root': {
                    color: theme.palette.primary.contrastText, 
                },
                '& .MuiSvgIcon-root': {
                    color: theme.palette.primary.contrastText
                },
            },
        }),
    }),
);

function Sidebar() {
    const theme = useTheme();
    const [open, setOpen] = React.useState(false);

    const toggleDrawer = () => {
        setOpen(!open);
    };

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <Drawer variant="permanent" open={open} >
                <DrawerHeader>
                    <IconButton onClick={toggleDrawer}>
                        {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                    </IconButton>
                </DrawerHeader>
                <Divider />
                <List>
                    <ListItem key='Dashboard' disablePadding sx={{ display: 'block' }}>
                        <ListItemButton
                            component={Link}
                            to="/dashboard"
                            sx={{
                                minHeight: 48,
                                justifyContent: open ? 'initial' : 'center',
                                px: 2.5,
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 0,
                                    mr: open ? 3 : 'auto',
                                    justifyContent: 'center',
                                }}
                            >
                                <DashboardIcon />
                            </ListItemIcon>
                            <ListItemText primary='Dashboard' sx={{ opacity: open ? 1 : 0 }} />
                        </ListItemButton>
                    </ListItem>
                </List>
                <Divider />
                <List>
                    <ListItem key='Admin' disablePadding sx={{ display: 'block' }}>
                        <ListItemButton
                            component={Link}
                            to="/admin"
                            sx={{
                                minHeight: 48,
                                justifyContent: open ? 'initial' : 'center',
                                px: 2.5,
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 0,
                                    mr: open ? 3 : 'auto',
                                    justifyContent: 'center',
                                }}
                            >
                                <SettingsIcon />
                            </ListItemIcon>
                            <ListItemText primary='Admin' sx={{ opacity: open ? 1 : 0 }} />
                        </ListItemButton>
                    </ListItem>
                </List>
                <Divider />
                <List className='sidebar-bottom'>
                    <ListItem key='Account' disablePadding sx={{ display: 'block' }}>
                        <ListItemButton
                            component={Link}
                            to="/account"
                            sx={{
                                minHeight: 48,
                                justifyContent: open ? 'initial' : 'center',
                                px: 2.5,
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 0,
                                    mr: open ? 3 : 'auto',
                                    justifyContent: 'center',
                                }}
                            >
                                <PersonIcon />
                            </ListItemIcon>
                            <ListItemText primary='Account' sx={{ opacity: open ? 1 : 0 }} />
                        </ListItemButton>
                    </ListItem>
                </List>
            </Drawer>
        </Box>
    );
}

export default Sidebar;
